import { DayOfWeek, TimeOfDay } from '@dreryk/egabinet-group-session-frontend-api';

import {
   commonTranslationKeys,
   createInterpolatedTranslationKey,
   TranslateFunction,
   useTranslate,
} from '@dreryk/react-common';
import {
   FilterPanelControllerCodecConfigArray,
   FilterPanelControllerCodecConfigSingleSelection,
   FilterPanelControllerDeselectorsPropsMapper,
} from '@dreryk/react-components';

import { translationKeys } from '../../../translations';
import { GroupSessionsFilterPanelSchedule } from '../../GroupSessionsFilterPanel.types';
import { TIMES_OF_DAY } from '../../GroupSessionsFilterPanel.constants';

import { ScheduleSearchParam } from './Schedule.types';

const TIMES_OF_DAY_TOTAL_COUNT = 3;
const DAYS_OF_WEEK: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

const SCHEDULE_TIME_OF_DAY_TO_SEARCH_PARAM: Record<TimeOfDay, number> = {
   MORNING: 0,
   AFTERNOON: 1,
   EVENING: 2,
};

export const getTimeOfDayByInternalId = (timeOfDayInternalId: number): TimeOfDay | null => {
   const internalIdsEntries = Object.entries(SCHEDULE_TIME_OF_DAY_TO_SEARCH_PARAM);
   const foundEntry = internalIdsEntries.find((entry) => entry.at(1) === timeOfDayInternalId);
   return foundEntry?.at(0) as TimeOfDay | null;
};

export const createScheduleDayOfWeekTriggerLabelExtractor = (
   translate: TranslateFunction
): ((chosenDayOfWeek: DayOfWeek) => string) => {
   return (chosenDayOfWeek) => {
      return translate(commonTranslationKeys.dayOfWeekMap[chosenDayOfWeek]);
   };
};

export const createScheduleTimesOfDayTriggerLabelExtractor = (
   translate: TranslateFunction
): ((timesOfDay: TimeOfDay[]) => string) => {
   return (timesOfDay) => {
      if (timesOfDay.length === 1) {
         return translate(translationKeys.timeOfDayMap[timesOfDay.at(0)!]);
      }
      const translationKey = createInterpolatedTranslationKey(translationKeys.chosenOptions, {
         count: timesOfDay.length,
         totalCount: TIMES_OF_DAY_TOTAL_COUNT,
      });
      return translate(translationKey);
   };
};

export const dayOfWeekControllerCodec: FilterPanelControllerCodecConfigSingleSelection<DayOfWeek> = {
   type: 'single-selection',
   keyGenerator: (dayOfWeek) => dayOfWeek ?? undefined,
   items: DAYS_OF_WEEK,
   mode: 'string',
};

export const getDayOfWeekDeselectorsProps: FilterPanelControllerDeselectorsPropsMapper<
   DayOfWeek,
   ScheduleSearchParam
> = (dayOfWeek, context) => {
   const currentTimesOfWeekParam = context.getParamValue('pora-dnia');

   if (currentTimesOfWeekParam) {
      return [];
   }

   return [
      {
         targetValue: null,
         label: commonTranslationKeys.dayOfWeekMap[dayOfWeek],
         testId: `scheduleDayOfWeek-${dayOfWeek}`,
      },
   ];
};

export const timesOfDayControllerCodec: FilterPanelControllerCodecConfigArray<TimeOfDay> = {
   type: 'array',
   keyGenerator: (timeOfDay) => SCHEDULE_TIME_OF_DAY_TO_SEARCH_PARAM[timeOfDay],
   items: TIMES_OF_DAY,
};

export const useTimesOfDayDeselectorsPropsMapper = (): FilterPanelControllerDeselectorsPropsMapper<
   GroupSessionsFilterPanelSchedule['timesOfDay'],
   ScheduleSearchParam
> => {
   const translate = useTranslate();

   return (timesOfDay, context) => {
      if (!timesOfDay.length) {
         return [];
      }
      const dayOfWeekParamValue = context.getParamValue('dzien');

      return timesOfDay.map((timeOfDay) => {
         const nextValue = timesOfDay.filter((currentTimeOfDay) => currentTimeOfDay !== timeOfDay);
         const onClick = !nextValue.length && dayOfWeekParamValue ? () => context.resetFilter('dzien') : undefined;

         const label = dayOfWeekParamValue
            ? createInterpolatedTranslationKey(translationKeys.scheduleFilterDeselectorLabel, {
                 dayOfWeek: translate(commonTranslationKeys.dayOfWeekMap[dayOfWeekParamValue as DayOfWeek]),
                 timeOfDay: translate(translationKeys.timeOfDayMap[timeOfDay]),
              })
            : translationKeys.timeOfDayMap[timeOfDay];

         return {
            targetValue: nextValue,
            label: label,
            testId: `scheduleTimeOfDay-${timeOfDay}`,
            onDeselection: onClick,
         };
      });
   };
};
